import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!token) toast.error("Missing reset token");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8 || !/\d/.test(password)) {
      toast.error("Password must be 8+ characters and include a digit");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/reset-password", { token, new_password: password });
      localStorage.setItem("gf_token", data.token);
      await refresh();
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/dashboard"), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-md mx-auto px-5 lg:px-8 py-16">
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto"><CheckCircle2 size={24} /></div>
            <h1 className="font-display text-3xl font-bold mt-6">Password updated</h1>
            <p className="text-neutral-600 mt-2">Signing you in…</p>
          </div>
        ) : (
          <>
            <div className="overline mb-3">RESET PASSWORD</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mb-2">Choose a new password</h1>
            <p className="text-neutral-600 mb-8">Minimum 8 characters, including at least one digit.</p>
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="text-sm font-medium">New password</label>
                <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                  data-testid="reset-password" autoComplete="new-password" />
              </div>
              <div>
                <label className="text-sm font-medium">Confirm password</label>
                <input required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                  data-testid="reset-confirm" autoComplete="new-password" />
              </div>
              <button disabled={loading || !token} className="btn-primary w-full justify-center" data-testid="reset-submit">
                {loading && <Loader2 size={16} className="animate-spin" />} <KeyRound size={16} /> Update password
              </button>
              <p className="text-xs text-neutral-500 text-center">
                Prefer to log in? <Link to="/login" className="underline">Back to login</Link>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
